import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Chip, CircularProgress, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ClassicLiveStage from "../components/ClassicLiveStage";
import BoardLiveView from "../components/BoardLiveView";
import SceneLiveView from "../components/SceneLiveView";
import { segmentsSentenceCount, turnAtSentence } from "../vendor/liveTapRenderer";
import { createAppbackResourceAdapter } from "../liveTap/appbackResourceAdapter";
import { createLinkFeedClient, isLinkGone } from "../liveTap/liveFeedClient";
import { TOOL_BTN_SX, TOOL_BTN_ACTIVE_SX, statusTone } from "../liveTap/liveChrome";
import { DEFAULT_LIVE_SYSTEM, liveSystemInfo } from "../liveTap/liveSystem";
import useLiveTranscript from "../hooks/useLiveTranscript";
import AppBackService from "../services/appback";
import stage from "./LiveTapPage.module.scss";
import styles from "./PublicLiveTapPage.module.scss";

/**
 * TT-1110 — the public, view-only Live Tapestry page at /live/:token.
 *
 * Rendered for anyone, signed in or not: the token in the URL is the whole
 * credential, and everything it reaches is a read (see
 * liveTap/liveFeedClient.js — the link client has no advance). The three
 * rendering systems draw exactly as they do for the host, through the same
 * components; what is missing is the host's chrome — transport, capture bar,
 * background picker, export, the system switcher, dev mode. Viewers get the
 * canvas and the read-only transcript panel (decision 3), and always follow
 * the live edge.
 *
 * The link's meta is read once, for the rendering system the host chose and
 * whether the link still stands. Then every system follows the doc's live
 * cassette through the link's doc read and band read (TT-1273), every 10 s
 * (the host: 5 s): classic draws from it, the scene and board open on it and
 * show it in their transcript panel. The cassette's state is the meeting's
 * status; a revoked or expired link answers 410 on the next read. The scene
 * and board poll their planned board through the view.
 */

const VIEWER_POLL_MS = 10000;

function statusLabel(done) {
  return done ? "ended" : "live";
}

// What the link answered when it was opened: a page state, not an error.
function linkStateFor(res) {
  if (!res?.error) return "ok";
  if (res.status === 404) return "missing";
  if (isLinkGone(res)) return "gone";
  return "error";
}

const NOTICES = {
  missing: {
    title: "This live link doesn’t exist",
    body: "Check the link you were given — it may have been copied incompletely.",
  },
  gone: {
    title: "This live link has ended",
    body: "The host closed the link, or it expired. Ask them for a new one.",
  },
  error: {
    title: "The live tapestry can’t be reached right now",
    body: "Try again in a moment.",
  },
};

const PublicLiveTapPage = () => {
  const { token } = useParams();
  const [linkState, setLinkState] = useState("loading");
  const [meta, setMeta] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Open the link: which system draws it, and whether it stands at all.
  useEffect(() => {
    let active = true;
    if (!token) {
      setLinkState("missing");
      return undefined;
    }
    AppBackService.getLiveLink(token).then((res) => {
      if (!active) return;
      setLinkState(linkStateFor(res));
      if (!res?.error) setMeta(res);
    });
    return () => {
      active = false;
    };
  }, [token]);

  const liveSystem = meta?.liveSystem || DEFAULT_LIVE_SYSTEM;
  const system = liveSystemInfo(liveSystem);
  const open = linkState === "ok";
  const feed = useMemo(() => (open ? createLinkFeedClient(token) : null), [open, token]);
  const resources = useMemo(() => createAppbackResourceAdapter(AppBackService.liveLinkCatalog(token)), [token]);

  // Every system reads the transcript here: classic draws from it, the
  // scene's opening is classic on paper (TT-1133), the board's opening
  // sketch letters its head, and the scene and board show it in their
  // transcript panel (TT-1215).
  const {
    segments,
    title: docTitle,
    createdAt,
    status,
    done,
    loading,
    error,
    errorStatus,
  } = useLiveTranscript(open ? feed : null, {
    autoRefresh: true,
    intervalMs: VIEWER_POLL_MS,
  });
  useEffect(() => {
    if (isLinkGone({ status: errorStatus })) setLinkState("gone");
  }, [errorStatus]);

  // Viewers always follow the live edge — the playhead is the last sentence.
  const totalSentences = useMemo(() => segmentsSentenceCount(segments), [segments]);
  const currentSent = totalSentences - 1;
  const currentTurn = useMemo(() => turnAtSentence(segments, currentSent), [segments, currentSent]);

  const title = docTitle || meta?.title || "Live Tap";
  const when = createdAt || meta?.createdAt;
  const docDate = useMemo(() => {
    const created = when ? dayjs(when) : null;
    return created?.isValid() ? created.format("M/D/YYYY") : "";
  }, [when]);
  const closed = done;

  if (linkState === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!open) {
    const notice = NOTICES[linkState] || NOTICES.error;
    return (
      <div className={styles.page}>
        <div className={styles.center}>
          <div className={styles.notice}>
            <span className={styles.brand}>Live Tapestry</span>
            <h1>{notice.title}</h1>
            <p>{notice.body}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.brand}>Live Tapestry</span>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{title}</h1>
          {docDate && <span className={styles.date}>{docDate}</span>}
        </div>
        <Chip size="small" color={statusTone(status)} label={statusLabel(done)} />
        {system.panel && (
          <Tooltip title={system.panel}>
            <span>
              <IconButton
                onClick={() => setInfoOpen((value) => !value)}
                sx={infoOpen ? TOOL_BTN_ACTIVE_SX : TOOL_BTN_SX}
                aria-label="Show the transcript">
                <InfoOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </header>

      {error && <div className={stage.errorBanner}>{error}</div>}

      <main className={styles.shell}>
        {liveSystem === "scene" && (
          <SceneLiveView
            feed={feed}
            resources={resources}
            docTitle={title}
            docDate={docDate}
            segments={segments}
            currentSent={currentSent}
            currentTurn={currentTurn}
            closed={closed}
            infoOpen={infoOpen}
            onInfoOpenChange={setInfoOpen}
          />
        )}
        {liveSystem === "board" && (
          <BoardLiveView
            feed={feed}
            resources={resources}
            docTitle={title}
            docDate={docDate}
            segments={segments}
            currentSent={currentSent}
            currentTurn={currentTurn}
            closed={closed}
            infoOpen={infoOpen}
            onInfoOpenChange={setInfoOpen}
          />
        )}
        {liveSystem === "classic" &&
          (loading ? (
            <div className={stage.emptyState}>
              <CircularProgress />
              <span>Loading live tap</span>
            </div>
          ) : (
            <ClassicLiveStage
              segments={segments}
              currentSent={currentSent}
              meetingName={title}
              meetingDate={docDate}
              resources={resources}
              infoOpen={infoOpen}
              // The renderer's debug panel defaults ON; a viewer never gets it.
              canvasProps={{ currentTurn, debugTools: false }}
            />
          ))}
      </main>
    </div>
  );
};

export default PublicLiveTapPage;
